import { useEffect, useMemo, useRef, useState } from "react";

import type { Props } from "./types";

import { Spinner } from "../spinner";
import { buildFrameSrcDoc, FRAME_SANDBOX, isScrolledToBottom } from "./documentFrame";
import style from "./style.module.scss";

export function HTMLDocumentReview(props: Props) {
	const { document: propDocument, loading, allowScripts = true, frameTitle = "Document", ...inputProps } = props;
	const { id, value, changeHandler } = inputProps;
	const frameRef = useRef<HTMLIFrameElement>(null);
	const [reviewed, setReviewed] = useState(value);

	const srcDoc = useMemo(() => buildFrameSrcDoc(propDocument, allowScripts), [propDocument, allowScripts]);

	useEffect(() => {
		changeHandler(!!reviewed, id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reviewed]);

	useEffect(() => {
		//If already read, no need to keep tracking scrolling
		if (reviewed) return;
		function onMessage(event: MessageEvent) {
			//The frame is cross origin, so it reports its scroll position instead of us reading it
			if (event.source !== frameRef.current?.contentWindow) return;
			if (isScrolledToBottom(event.data)) setReviewed(true);
		}
		window.addEventListener("message", onMessage);
		return () => window.removeEventListener("message", onMessage);
	}, [reviewed]);

	if (loading) {
		return <Spinner tag="h5">Loading Document</Spinner>;
	}
	return (
		<div className={style.documentDisplay}>
			<iframe
				className={style.documentFrame}
				ref={frameRef}
				sandbox={FRAME_SANDBOX}
				srcDoc={srcDoc}
				title={frameTitle}
			/>
		</div>
	);
}
